// Path: frontend/src/pages/admin/ProgressTrackingPage.tsx
// 研发进度跟踪页面

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ChevronDown, ChevronRight, FileText, Layers, 
  Loader2, Search, RefreshCw, Coins, User, Calendar
} from 'lucide-react';
import { 
  getCourseProgress, 
  type CourseProgress, 
  type StageProgress, 
  type LessonProgress,
  type PlanProgress,
  type SlideProgress 
} from '../../lib/api/progress';
import { api } from '../../lib/api';
import LogoutButton from '../../components/common/LogoutButton';

const CANDY_COLORS = [
  '#FF6B6B', '#4ECDC4', '#FFD93D', '#6C5CE7', '#FF8C42',
  '#00B894', '#E17055', '#74B9FF', '#FDCB6E', '#A29BFE',
  '#FF7675', '#00CEC9', '#FAB1A0', '#81ECEC', '#FFEAA7',
  '#DFE6E9', '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3',
  '#FF6B81', '#1DD1A1', '#FECA57', '#5F27CD', '#FF9F43',
  '#10AC84', '#EE5A24', '#48DBFB', '#C4E538', '#8395A7',
  '#FC5C65', '#0ABDE3', '#FED330', '#3D3D3D', '#FA8231',
  '#20BF6B', '#EB3B5A', '#45AAF2', '#F7B731', '#4B6584',
  '#FC5C65', '#26DE81', '#FD9644', '#A55EEA', '#4B7BEC',
  '#EF5B5B', '#2BCBBA', '#F3B431', '#778CA3', '#FC5C65',
  '#26DE81', '#45AAF2', '#FED330', '#2BCBBA', '#EB3B5A',
  '#A55EEA', '#FD9644', '#4B7BEC', '#F7B731', '#20BF6B',
  '#FA8231', '#4B6584', '#C4E538', '#48DBFB', '#EE5A24',
  '#10AC84', '#FF9F43', '#5F27CD', '#FECA57', '#1DD1A1',
  '#FF6B81', '#00D2D3', '#5F27CD', '#54A0FF', '#FF9FF3',
  '#DFE6E9', '#FFEAA7', '#81ECEC', '#FAB1A0', '#00CEC9'
];

const FIXED_GRAY = '#9CA3AF';
const FIXED_GREEN = '#10B981';

const getLessonColor = (lessonId: number, progress: number): string => {
  if (progress === 0) {
    return FIXED_GRAY;
  }
  if (progress === 100) {
    return FIXED_GREEN;
  }
  const goldenRatio = 1.618033988749895;
  const index = Math.floor((lessonId * goldenRatio) % CANDY_COLORS.length);
  return CANDY_COLORS[index];
};

const RunningPerson: React.FC<{ progress: number; color: string }> = ({ progress, color }) => {
  return (
    <div 
      className="absolute top-1/2 -translate-y-1/2 transition-all duration-300"
      style={{ left: `${Math.max(0, Math.min(progress - 2, 98))}%` }}
    >
      <svg 
        width="24" 
        height="24" 
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="4" r="3" fill={color} stroke={color} strokeWidth="1"/>
        <line x1="12" y1="7" x2="12" y2="13" stroke={color} strokeWidth="2" strokeLinecap="round"/>
        <g>
          <line 
            x1="12" y1="13" x2="8" y2="18" 
            stroke={color} 
            strokeWidth="2" 
            strokeLinecap="round"
            style={{
              transformOrigin: '12px 13px',
              animation: 'legSwing 0.3s ease-in-out infinite alternate'
            }}
          />
          <line 
            x1="12" y1="13" x2="16" y2="18" 
            stroke={color} 
            strokeWidth="2" 
            strokeLinecap="round"
            style={{
              transformOrigin: '12px 13px',
              animation: 'legSwing 0.3s ease-in-out infinite alternate-reverse'
            }}
          />
        </g>
        <g>
          <line 
            x1="12" y1="9" x2="8" y2="11" 
            stroke={color} 
            strokeWidth="2" 
            strokeLinecap="round"
            style={{
              transformOrigin: '12px 9px',
              animation: 'armSwing 0.3s ease-in-out infinite alternate-reverse'
            }}
          />
          <line 
            x1="12" y1="9" x2="16" y2="11" 
            stroke={color} 
            strokeWidth="2" 
            strokeLinecap="round"
            style={{
              transformOrigin: '12px 9px',
              animation: 'armSwing 0.3s ease-in-out infinite alternate'
            }}
          />
        </g>
        <style>
          {`
            @keyframes legSwing {
              0% { transform: rotate(-20deg); }
              100% { transform: rotate(20deg); }
            }
            @keyframes armSwing {
              0% { transform: rotate(-25deg); }
              100% { transform: rotate(25deg); }
            }
          `}
        </style>
      </svg>
    </div>
  );
};

const ProgressBar3D: React.FC<{ completed: number; total: number; lessonId: number }> = ({ completed, total, lessonId }) => {
  if (total === 0) return null;
  
  const progress = (completed / total) * 100;
  const showRunner = progress > 0 && progress < 100;
  const color = getLessonColor(lessonId, progress);
  
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-purple-700 whitespace-nowrap">
        {completed}/{total}
      </span>
      <div className="relative w-48 h-6 bg-gray-200 rounded-full overflow-hidden shadow-inner border border-gray-300">
        <div 
          className="absolute inset-0 bg-gradient-to-b from-gray-100 to-gray-300 opacity-50"
          style={{ zIndex: 1 }}
        />
        <div 
          className="absolute top-0 left-0 h-full rounded-full transition-all duration-500 shadow-lg"
          style={{ 
            width: `${progress}%`,
            background: `linear-gradient(to bottom, ${color}99, ${color})`,
            boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.3), 0 1px 3px rgba(0,0,0,0.2)'
          }}
        >
          <div 
            className="absolute inset-0 bg-gradient-to-b from-white/30 to-transparent rounded-full"
            style={{ height: '50%' }}
          />
        </div>
        {showRunner && <RunningPerson progress={progress} color={color} />}
      </div>
    </div>
  );
};

type Course = {
  id: number;
  title: string;
};

const ProgressTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [progressData, setProgressData] = useState<CourseProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [expandedLessons, setExpandedLessons] = useState<Set<number>>(new Set());
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setIsLoadingCourses(true);
    try {
      const res = await api.get('/courses/');
      setCourses(res.data);
      if (res.data.length > 0) {
        setSelectedCourseId(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setIsLoadingCourses(false);
    }
  };

  useEffect(() => {
    if (selectedCourseId) {
      fetchProgress(selectedCourseId);
    }
  }, [selectedCourseId, selectedDate]);

  const fetchProgress = async (courseId: number) => {
    setIsLoading(true);
    try {
      const queryDate = selectedDate ? new Date(selectedDate) : undefined;
      const data = await getCourseProgress(courseId, queryDate);
      setProgressData(data);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStage = (stageId: number) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(stageId)) {
        next.delete(stageId);
      } else {
        next.add(stageId);
      }
      return next;
    });
  };

  const toggleLesson = (lessonId: number) => {
    setExpandedLessons(prev => {
      const next = new Set(prev);
      if (next.has(lessonId)) {
        next.delete(lessonId);
      } else {
        next.add(lessonId);
      }
      return next;
    });
  };

  const formatCost = (cost: number) => {
    return `¥${cost.toFixed(4)}`;
  };

  const formatTokens = (tokens: number) => {
    return tokens.toLocaleString();
  };

  const renderPlanProgress = (plan: PlanProgress | null) => {
    if (!plan) {
      return (
        <div className="text-gray-400 text-xs italic">
          暂无 Plan 数据
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${plan.is_final ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
            {plan.version_number}
          </span>
          <span className="text-gray-500">{plan.is_final ? '定稿' : '草稿'}</span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600">内容字数: {plan.content_length.toLocaleString()}</span>
          <span className="text-gray-400">|</span>
          <User size={12} className="text-gray-400" />
          <span className="text-gray-600">{plan.creator_name}</span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-purple-50 rounded p-2">
            <span className="text-gray-500">Token: </span>
            <span className="font-medium text-purple-700">{formatTokens(plan.plan_tokens)}</span>
          </span>
          <span className="bg-green-50 rounded p-2">
            <span className="text-gray-500">费用: </span>
            <span className="font-medium text-green-700">{formatCost(plan.plan_cost_rmb)}</span>
          </span>
        </div>
      </div>
    );
  };

  const renderSlideProgress = (slide: SlideProgress | null) => {
    if (!slide) {
      return (
        <div className="text-gray-400 text-xs italic">
          暂无 Slide 数据
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-purple-50 rounded p-2">
            <span className="text-gray-500">Token: </span>
            <span className="font-medium text-purple-700">{formatTokens(slide.slide_tokens)}</span>
          </span>
          <span className="bg-green-50 rounded p-2">
            <span className="text-gray-500">费用: </span>
            <span className="font-medium text-green-700">{formatCost(slide.slide_cost_rmb)}</span>
          </span>
        </div>
      </div>
    );
  };

  const renderLesson = (lesson: LessonProgress, globalIndex: number) => {
    const isExpanded = expandedLessons.has(lesson.lesson_id);
    const slide = lesson.slide_progress;

    return (
      <div key={lesson.lesson_id} className="border border-gray-200 rounded-lg overflow-hidden">
        <div 
          className="bg-gray-50 p-3 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => toggleLesson(lesson.lesson_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <span className="text-gray-500 text-sm">L{globalIndex + 1}</span>
              <span className="font-medium text-gray-800">{lesson.lesson_title}</span>
            </div>
            <div className="flex items-center gap-4">
              {lesson.plan_progress?.creator_name && (
                <span className="text-xs text-gray-500">作者: {lesson.plan_progress.creator_name}</span>
              )}
              {slide && (
                <ProgressBar3D completed={slide.completed_slides} total={slide.total_slide_indices} lessonId={lesson.lesson_id} />
              )}
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>当日Token: {formatTokens(lesson.daily_tokens)}</span>
                <span>总Token: {formatTokens(lesson.total_tokens)}</span>
                <span className="text-green-600 font-medium">当日金额 {formatCost(lesson.daily_cost_rmb)}</span>
                <span className="text-green-600 font-medium">总金额 {formatCost(lesson.total_cost_rmb)}</span>
              </div>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 space-y-4 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-blue-600" />
                    <span className="font-medium text-gray-700">Plan 进度</span>
                    {lesson.plan_progress && (
                      <>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${lesson.plan_progress.is_final ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {lesson.plan_progress.version_number}
                        </span>
                        <span className="text-gray-500 text-xs">{lesson.plan_progress.is_final ? '定稿' : '草稿'}</span>
                        <span className="text-gray-400 text-xs">|</span>
                        <span className="text-gray-600 text-xs">内容字数: {lesson.plan_progress.content_length.toLocaleString()}</span>
                      </>
                    )}
                  </div>
                </div>
                {lesson.plan_progress ? (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="bg-purple-50 rounded p-2">
                      <span className="text-gray-500">Token: </span>
                      <span className="font-medium text-purple-700">{formatTokens(lesson.plan_progress.plan_tokens)}</span>
                    </span>
                    <span className="bg-green-50 rounded p-2">
                      <span className="text-gray-500">费用: </span>
                      <span className="font-medium text-green-700">{formatCost(lesson.plan_progress.plan_cost_rmb)}</span>
                    </span>
                    <span className="text-gray-400">|</span>
                    <User size={12} className="text-gray-400" />
                    <span className="text-gray-600">{lesson.plan_progress.creator_name}</span>
                  </div>
                ) : (
                  <div className="text-gray-400 text-xs italic">暂无 Plan 数据</div>
                )}
              </div>
              
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <Layers size={16} className="text-purple-600" />
                  <span className="font-medium text-gray-700">Slide 进度</span>
                  {lesson.slide_progress && (
                    <span className="text-sm font-medium text-purple-700">
                      {lesson.slide_progress.completed_slides}/{lesson.slide_progress.total_slide_indices}
                    </span>
                  )}
                </div>
                {renderSlideProgress(lesson.slide_progress)}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStage = (stage: StageProgress, startIndex: number) => {
    const isExpanded = expandedStages.has(stage.stage_id);
    
    const completedLessons = stage.lessons.filter(l => {
      const slide = l.slide_progress;
      return slide && slide.total_slide_indices > 0 && slide.completed_slides === slide.total_slide_indices;
    }).length;
    const completedPlans = stage.lessons.filter(l => {
      const plan = l.plan_progress;
      return plan && plan.content_length > 0;
    }).length;
    const inProgressLessons = stage.lessons.filter(l => {
      const slide = l.slide_progress;
      return slide && slide.completed_slides > 0 && slide.completed_slides < slide.total_slide_indices;
    }).length;
    const notStartedLessons = stage.lessons.length - completedLessons - completedPlans;

    return (
      <div key={stage.stage_id} className="border border-gray-300 rounded-lg overflow-hidden">
        <div 
          className="bg-gradient-to-r from-gray-100 to-gray-50 p-4 cursor-pointer hover:from-gray-200 hover:to-gray-100 transition-colors"
          onClick={() => toggleStage(stage.stage_id)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              <span className="font-semibold text-gray-800 text-lg">{stage.stage_title}</span>
              <span className="text-sm text-gray-500">({stage.lessons.length} 课时)</span>
              <span className="text-sm text-gray-400">|</span>
              <span className="text-xs">
                <span className="text-green-600 font-medium">已完成 {completedLessons}</span>
                <span className="text-gray-300 mx-1">·</span>
                <span className="text-blue-600 font-medium">教案 {completedPlans}</span>
                <span className="text-gray-300 mx-1">·</span>
                <span className="text-yellow-600 font-medium">研发中 {inProgressLessons}</span>
                <span className="text-gray-300 mx-1">·</span>
                <span className="text-gray-500 font-medium">未开始 {notStartedLessons}</span>
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-500">
                当日Token: {formatTokens(stage.daily_tokens)} | 总Token: {formatTokens(stage.stage_total_tokens)}
              </span>
              <span className="text-green-600 font-semibold">当日金额 {formatCost(stage.daily_cost_rmb)}</span>
              <span className="text-green-600 font-semibold">总金额 {formatCost(stage.stage_total_cost_rmb)}</span>
            </div>
          </div>
        </div>
        
        {isExpanded && (
          <div className="p-4 space-y-3 bg-white">
            {stage.lessons.map((lesson, idx) => renderLesson(lesson, startIndex + idx))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} />
              返回
            </button>
            <h1 className="text-xl font-bold text-gray-800">研发进度跟踪</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => selectedCourseId && fetchProgress(selectedCourseId)}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              刷新
            </button>
            <LogoutButton />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Search size={18} className="text-gray-400" />
              <span className="text-gray-700 font-medium">选择课程:</span>
            </div>
            {isLoadingCourses ? (
              <Loader2 className="animate-spin text-gray-400" />
            ) : (
              <select
                value={selectedCourseId || ''}
                onChange={(e) => setSelectedCourseId(Number(e.target.value))}
                className="flex-1 max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.title}</option>
                ))}
              </select>
            )}
            <div className="flex items-center gap-2">
              <Calendar size={18} className="text-gray-400" />
              <span className="text-gray-700 font-medium">选择日期:</span>
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={(() => {
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              })()}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-blue-600 mr-2" />
            <span className="text-gray-500">加载进度数据...</span>
          </div>
        ) : progressData ? (
          <>
            {(() => {
              const allLessons = progressData.stages.flatMap(s => s.lessons);
              const totalLessons = allLessons.length;
              const completedLessons = allLessons.filter(l => {
                const slide = l.slide_progress;
                return slide && slide.total_slide_indices > 0 && slide.completed_slides === slide.total_slide_indices;
              }).length;
              const completedPlans = allLessons.filter(l => {
                const plan = l.plan_progress;
                return plan && plan.content_length > 0;
              }).length;
              const inProgressLessons = allLessons.filter(l => {
                const slide = l.slide_progress;
                return slide && slide.completed_slides > 0 && slide.completed_slides < slide.total_slide_indices;
              }).length;
              const notStartedLessons = totalLessons - completedLessons - completedPlans;

              return (
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 mb-6 text-white">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold">{progressData.course_title}</h2>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-white/10 rounded-lg p-3">
                      <div className="text-white/70 text-sm">总课时数</div>
                      <div className="text-2xl font-bold">{totalLessons}</div>
                    </div>
                    <div className="bg-green-500/30 rounded-lg p-3 border border-green-400/50">
                      <div className="text-green-200 text-sm">已完成</div>
                      <div className="text-2xl font-bold text-green-100">{completedLessons}</div>
                    </div>
                    <div className="bg-blue-500/30 rounded-lg p-3 border border-blue-400/50">
                      <div className="text-blue-200 text-sm">完成教案</div>
                      <div className="text-2xl font-bold text-blue-100">{completedPlans}</div>
                    </div>
                    <div className="bg-yellow-500/30 rounded-lg p-3 border border-yellow-400/50">
                      <div className="text-yellow-200 text-sm">研发中</div>
                      <div className="text-2xl font-bold text-yellow-100">{inProgressLessons}</div>
                    </div>
                    <div className="bg-gray-500/30 rounded-lg p-3 border border-gray-400/50">
                      <div className="text-gray-300 text-sm">未开始</div>
                      <div className="text-2xl font-bold text-gray-200">{notStartedLessons}</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20 flex items-center justify-center gap-6 text-sm">
                    <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                      <span className="text-white/70">当日Token:</span>
                      <span className="font-semibold">{formatTokens(progressData.daily_tokens)}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                      <span className="text-white/70">总Token:</span>
                      <span className="font-semibold">{formatTokens(progressData.course_total_tokens)}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                      <Coins size={16} />
                      <span className="text-white/70">当日金额:</span>
                      <span className="font-semibold">{formatCost(progressData.daily_cost_rmb)}</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                      <Coins size={16} />
                      <span className="text-white/70">总金额:</span>
                      <span className="font-semibold">{formatCost(progressData.course_total_cost_rmb)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}

            <div className="space-y-4">
              {(() => {
                let startIndex = 0;
                return progressData.stages.map((stage) => {
                  const stageElement = renderStage(stage, startIndex);
                  startIndex += stage.lessons.length;
                  return stageElement;
                });
              })()}
            </div>
          </>
        ) : (
          <div className="text-center py-20 text-gray-500">
            请选择一个课程查看进度
          </div>
        )}
      </main>
    </div>
  );
};

export default ProgressTrackingPage;
